import React, { useState, useEffect } from "react";
import { useAuth } from "AuthContext";
import { useMemo } from "react";
import PropTypes from "prop-types";
import { Card, Grid, MenuItem, Select, FormControl } from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { useParams } from "react-router-dom";

// Constants for auto‑calculations
const PF_RATE = 0.25;         // Basic × 25%
const ESI_RATE = 0.04;        // Total Monthly × 4%
const LTA_FIXED = 34;         // Fixed monthly amount for Labour Welfare Fund

const salaryComponents = [
  { key: "basic_salary", label: "Basic Salary" },
  { key: "hra", label: "House Rent Allowance (HRA)" },
  { key: "dearness_allowance", label: "Dearness Allowance" },
  { key: "conveyance_allowance", label: "Conveyance Allowance" },
];

const deductionComponents = [
  { key: "pf", label: "Provident Fund (PF)", formula: "" },
  { key: "esi", label: "Employee State Insurance (ESI)", formula: "" },
  { key: "lta", label: "Labour Welfare Fund" },
];

const selectData = {
  currencies: [
    { value: "USD", label: "USD", symbol: "$" },
    { value: "INR", label: "INR", symbol: "₹" },
    { value: "EUR", label: "EUR", symbol: "€" },
    { value: "GBP", label: "GBP", symbol: "£" },
    { value: "JPY", label: "JPY", symbol: "¥" },
    { value: "AUD", label: "AUD", symbol: "A$" },
    { value: "CAD", label: "CAD", symbol: "C$" },
  ],
};

function SalaryStructure({ value = {}, onChange, onUpdate, loading, setLoading, onNotify }) {
  const { permissions: ctxPerms = [] } = useAuth();
  const PF_KEY = "pf";
  const ESI_KEY = "esi";
  const LTA_KEY = "lta";

  const storedPerms = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("permissions") || "[]");
    } catch {
      return [];
    }
  }, []);

  const permSet = useMemo(() => {
    const perms = ctxPerms.length ? ctxPerms : storedPerms;
    return new Set(perms.map((p) => (typeof p === "string" ? p : p.name)));
  }, [ctxPerms, storedPerms]);

  const canField = (key) => permSet.has(`UserProfile.SalaryStructure.${key}`);

  const id = useParams();
  const [selectedCurrency, setSelectedCurrency] = useState("INR");
  const [currencySymbol, setCurrencySymbol] = useState("₹");
  const [formData, setFormData] = useState({ ...value });
  const [errors, setErrors] = useState({});

  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "Confirm Salary Structure Update",
    message: "Are you sure you want to update the salary structure?",
  });

  // -------- Central recalculation function ----------
  const recalculate = (data) => {
    const newData = { ...data };

    // 1. Fixed LTA
    newData.lta_monthly = LTA_FIXED.toString();
    newData.lta_annual = (LTA_FIXED * 12).toFixed(2);

    // 2. Calculate total monthly earnings (CTC)
    let totalMonthly = 0;
    salaryComponents.forEach(({ key }) => {
      const monthlyKey = `${key}_monthly`;
      const val = parseFloat(newData[monthlyKey] || 0);
      if (!isNaN(val)) totalMonthly += val;
    });
    newData.total_monthly = totalMonthly.toFixed(2);
    newData.total_annual = (totalMonthly * 12).toFixed(2);

    // 3. PF = Basic × PF_RATE
    const basicMonthly = parseFloat(newData.basic_salary_monthly || 0);

    // const pfMonthly = basicMonthly * PF_RATE;
    // newData.pf_monthly = pfMonthly.toFixed(2);
    // newData.pf_annual = (pfMonthly * 12).toFixed(2);

    if (newData.pf === "yes") {
      const pfMonthly = basicMonthly * PF_RATE;
      newData.pf_monthly = pfMonthly.toFixed(2);
      newData.pf_annual = (pfMonthly * 12).toFixed(2);
    } else {
      newData.pf_monthly = "0.00";
      newData.pf_annual = "0.00";
    }

    // 4. ESI = Total Monthly × ESI_RATE
    // const esiMonthly = totalMonthly * ESI_RATE;
    // newData.esi_monthly = esiMonthly.toFixed(2);
    // newData.esi_annual = (esiMonthly * 12).toFixed(2);

    if (newData.esi === "yes") {
      const esiMonthly = totalMonthly * ESI_RATE;
      newData.esi_monthly = esiMonthly.toFixed(2);
      newData.esi_annual = (esiMonthly * 12).toFixed(2);
    } else {
      newData.esi_monthly = "0.00";
      newData.esi_annual = "0.00";
    }

    // 5. Annual values for all salary components
    salaryComponents.forEach(({ key }) => {
      const monthlyKey = `${key}_monthly`;
      const annualKey = `${key}_annual`;
      const monthlyVal = parseFloat(newData[monthlyKey] || 0);
      newData[annualKey] = (monthlyVal * 12).toFixed(2);
    });

    // 6. Annual values for any other deductions (only LTA left, but we already did it)
    deductionComponents.forEach(({ key }) => {
      if (key === "pf" || key === "esi" || key === "lta") return;
      const monthlyKey = `${key}_monthly`;
      const annualKey = `${key}_annual`;
      const monthlyVal = parseFloat(newData[monthlyKey] || 0);
      newData[annualKey] = (monthlyVal * 12).toFixed(2);
    });

    return newData;
  };
  // -------------------------------------------------

  // Initialize form data (and recalc to ensure formulas are applied)
  useEffect(() => {
    const initialData = { ...value };
    const recalculated = recalculate(initialData);
    setFormData(recalculated);
    if (value.currency) {
      setSelectedCurrency(value.currency);
      const found = selectData.currencies.find((c) => c.value === value.currency);
      if (found) setCurrencySymbol(found.symbol);
    }
  }, [value]);

  const handleCurrencyChange = (event) => {
    const selected = event.target.value;
    setSelectedCurrency(selected);
    const selectedCurrencyData = selectData.currencies.find((c) => c.value === selected);
    setCurrencySymbol(selectedCurrencyData?.symbol || "");
    if (onChange) {
      onChange({ ...value, currency: selected });
    }
  };

  const handleInputChange = (key, inputValue, isAutoCalculation = false) => {
    if (inputValue === "" || /^[0-9]*\.?[0-9]*$/.test(inputValue)) {
      setFormData((prev) => {
        const updated = { ...prev, [key]: inputValue };
        // Recalculate all derived fields based on the new value
        const recalculated = recalculate(updated);
        // Notify parent with the fully recalculated data
        onChange && onChange(recalculated);
        return recalculated;
      });
      if (errors[key]) {
        setErrors((prev) => ({ ...prev, [key]: "" }));
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    [...salaryComponents, ...deductionComponents].forEach(({ key }) => {
      const monthlyKey = `${key}_monthly`;
      const annualKey = `${key}_annual`;

      // if (key !== LTA_KEY && (!formData[monthlyKey] || parseFloat(formData[monthlyKey]) <= 0)) {
      //   newErrors[monthlyKey] = "Monthly amount is required";
      // }

      // Skip validation if PF/ESI not applicable
      if (
        key === "pf" && formData.pf !== "yes"
      ) {
        return;
      }

      if (
        key === "esi" && formData.esi !== "yes"
      ) {
        return;
      }

      if (key !== LTA_KEY && (!formData[monthlyKey] || parseFloat(formData[monthlyKey]) <= 0)) {
        newErrors[monthlyKey] = "Monthly amount is required";
      }

      if (formData[monthlyKey] && isNaN(parseFloat(formData[monthlyKey]))) {
        newErrors[monthlyKey] = "Must be a valid number";
      }

      if (formData[annualKey] && isNaN(parseFloat(formData[annualKey]))) {
        newErrors[annualKey] = "Must be a valid number";
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const calculateTotals = () => {
    let earningsMonthly = 0;
    let earningsAnnual = 0;
    let deductionsMonthly = 0;
    let deductionsAnnual = 0;

    // Earnings
    salaryComponents.forEach(({ key }) => {
      const monthlyKey = `${key}_monthly`;
      const annualKey = `${key}_annual`;
      earningsMonthly += parseFloat(formData[monthlyKey] || 0);
      earningsAnnual += parseFloat(formData[annualKey] || 0);
    });

    // Deductions
    deductionComponents.forEach(({ key }) => {
      const monthlyKey = `${key}_monthly`;
      const annualKey = `${key}_annual`;
      deductionsMonthly += parseFloat(formData[monthlyKey] || 0);
      deductionsAnnual += parseFloat(formData[annualKey] || 0);
    });

    const totalMonthly = earningsMonthly;
    const totalAnnual = earningsAnnual;
    const inHandMonthly = earningsMonthly - deductionsMonthly;
    const inHandAnnual = earningsAnnual - deductionsAnnual;

    return {
      earningsMonthly,
      earningsAnnual,
      deductionsMonthly,
      deductionsAnnual,
      totalMonthly,
      totalAnnual,
      inHandMonthly,
      inHandAnnual,
    };
  };

  const handleConfirmUpdate = () => {
    if (!validateForm()) {
      onNotify?.("Please fix the highlighted salary fields before saving.", "warning");
      return;
    }
    const totals = calculateTotals();
    setConfirmDialog({
      open: true,
      title: "Confirm Salary Structure Update",
      message: `Are you sure you want to update the salary structure?`,
      summary: { ...totals, currency: currencySymbol },
    });
  };

  const handleUpdate = () => {
    setConfirmDialog((prev) => ({ ...prev, open: false }));
    setLoading(true);
    const finalData = { ...formData };
    onUpdate(finalData);
  };

  const handleCloseDialog = () => {
    setConfirmDialog((prev) => ({ ...prev, open: false }));
  };

  const totals = calculateTotals();

  const formatNumber = (num) => {
    if (!num && num !== 0) return "";
    const number = typeof num === "string" ? parseFloat(num) : num;
    if (isNaN(number)) return "";
    return number.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const renderComponentRow = ({ key, label }) => {
    if (!canField(key)) return null;
    const keyMonthly = `${key}_monthly`;
    const keyAnnual = `${key}_annual`;
    const hasError = errors[keyMonthly] || errors[keyAnnual];

    return (
      <div className="row mb-2 align-items-center" key={key}>
        <div className="col-md-4">
          <SoftTypography
            variant="body2"
            fontWeight="medium"
            color={hasError ? "error" : "text"}
          >
            {label}
            {hasError && (
              <SoftTypography variant="caption" color="error" display="block" mt={0.5}>
                {errors[keyMonthly] || errors[keyAnnual]}
              </SoftTypography>
            )}
          </SoftTypography>
        </div>
        <div className="col-md-4">
          <SoftInput
            type="text"
            placeholder={`${currencySymbol} 0`}
            value={formData?.[keyMonthly] ?? ""}
            onChange={(e) => handleInputChange(keyMonthly, e.target.value)}
            error={!!errors[keyMonthly]}
            fullWidth
            size="small"
            sx={{ marginY: 0.5 }}
          />
        </div>
        <div className="col-md-4">
          <SoftInput
            type="text"
            placeholder={`${currencySymbol} 0`}
            value={formData?.[keyAnnual] ?? ""}
            fullWidth
            disabled
            InputProps={{ readOnly: true }}
            size="small"
            sx={{ marginY: 0.5 }}
          />
        </div>
      </div>
    );
  };

  const renderDeductionRow = ({ key, label, formula }) => {
    if (!canField(key)) return null;
    const keyMonthly = `${key}_monthly`;
    const keyAnnual = `${key}_annual`;
    const hasError = errors[keyMonthly] || errors[keyAnnual];
    const isAutoCalculated = key === PF_KEY || key === ESI_KEY || key === LTA_KEY;

    return (
      <div className="row mb-2 align-items-center" key={key}>
        <div className="col-md-4">
          <SoftTypography
            variant="body2"
            fontWeight="medium"
            color={hasError ? "error" : "text"}
          >
            {label}
            {formula && (
              <SoftTypography variant="caption" color="textSecondary" display="block">
                {formula}
              </SoftTypography>
            )}
            {hasError && (
              <SoftTypography variant="caption" color="error" display="block" mt={0.5}>
                {errors[keyMonthly] || errors[keyAnnual]}
              </SoftTypography>
            )}
          </SoftTypography>
        </div>
        <div className="col-md-4">
          <SoftInput
            type="text"
            placeholder={`${currencySymbol} 0`}
            value={formData?.[keyMonthly] ?? ""}
            onChange={(e) => !isAutoCalculated && handleInputChange(keyMonthly, e.target.value)}
            error={!!errors[keyMonthly]}
            fullWidth
            disabled
            size="small"
            sx={{ marginY: 0.5 }}
          />
        </div>
        <div className="col-md-4">
          <SoftInput
            type="text"
            placeholder={`${currencySymbol} 0`}
            value={formData?.[keyAnnual] ?? ""}
            fullWidth
            disabled
            size="small"
            sx={{ marginY: 0.5 }}
          />
        </div>
      </div>
    );
  };

  return (
    <>
      <Card id="salary-structure" className="p-3" sx={{ marginBottom: 2 }}>
        <SoftBox mb={2} display="flex" justifyContent="space-between" alignItems="center">
          <SoftTypography variant="h5">Salary Structure</SoftTypography>
          {canField("currency") && (
            <div className="col-md-2">
              <SoftTypography
                component="label"
                variant="caption"
                fontWeight="bold"
                className="text-capitalize mb-1 d-block"
              >
                Currency
              </SoftTypography>
              <FormControl fullWidth size="small">
                <Select value={selectedCurrency} onChange={handleCurrencyChange} size="small">
                  {selectData.currencies.map((currency) => (
                    <MenuItem key={currency.value} value={currency.value}>
                      {currency.label} ({currency.symbol})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          )}
        </SoftBox>

        {errors.consistency && (
          <SoftBox mb={2} p={1.5} bgcolor="error.light" borderRadius="md">
            <SoftTypography variant="caption" color="error" fontWeight="bold">
              ⚠️ {errors.consistency}
            </SoftTypography>
          </SoftBox>
        )}

        <div className="container-fluid">
          {/* Earnings Section */}
          <SoftBox mb={2}>
            <div className="row border-bottom pb-1 mb-2">
              <div className="col-md-4">
                <SoftTypography variant="body2" fontWeight="bold" color="textSecondary">
                  Salary Components
                </SoftTypography>
              </div>
              <div className="col-md-4">
                <SoftTypography variant="body2" fontWeight="bold" color="textSecondary">
                  Monthly ({currencySymbol})
                </SoftTypography>
              </div>
              <div className="col-md-4">
                <SoftTypography variant="body2" fontWeight="bold" color="textSecondary">
                  Annual ({currencySymbol})
                </SoftTypography>
              </div>
            </div>

            {salaryComponents.map(renderComponentRow)}

            {/* Total CTC after Conveyance */}
            <div className="row border-top align-items-center">
              <div className="col-md-4">
                <SoftTypography variant="body2" fontWeight="bold">
                  Total CTC
                </SoftTypography>
              </div>
              <div className="col-md-4">
                <SoftBox bgcolor="primary.light" borderRadius="md">
                  <SoftTypography variant="h6" fontWeight="bold">
                    {currencySymbol} {formatNumber(totals.earningsMonthly)}
                  </SoftTypography>
                </SoftBox>
              </div>
              <div className="col-md-4">
                <SoftBox bgcolor="primary.light" borderRadius="md">
                  <SoftTypography variant="h6" fontWeight="bold">
                    {currencySymbol} {formatNumber(totals.earningsAnnual)}
                  </SoftTypography>
                </SoftBox>
              </div>
            </div>
          </SoftBox>

          {/* Deductions Section */}
          <SoftBox mb={3}>{deductionComponents.map(renderDeductionRow)}</SoftBox>

          {/* Totals Section */}
          {canField("total") && (
            <SoftBox bgcolor="grey.50" borderRadius="md">
              <div className="row mb-1">
                <div className="col-md-4">
                  <SoftTypography variant="body2" fontWeight="bold">
                    Total Deductions
                  </SoftTypography>
                </div>
                <div className="col-md-4">
                  <SoftTypography variant="body2" fontWeight="bold" color="error">
                    - {currencySymbol} {formatNumber(totals.deductionsMonthly)}
                  </SoftTypography>
                </div>
                <div className="col-md-4">
                  <SoftTypography variant="body2" fontWeight="bold" color="error">
                    - {currencySymbol} {formatNumber(totals.deductionsAnnual)}
                  </SoftTypography>
                </div>
              </div>

              <hr className="my-2" />

              <div className="row mt-2">
                <div className="col-md-4">
                  <SoftTypography variant="h6" fontWeight="bold" color="info">
                    Net Salary (In-Hand)
                  </SoftTypography>
                </div>
                <div className="col-md-4">
                  <SoftBox bgcolor="info.light" borderRadius="md">
                    <SoftTypography variant="body2" color="info" fontWeight="bold">
                      {currencySymbol} {formatNumber(totals.inHandMonthly)}
                    </SoftTypography>
                  </SoftBox>
                </div>
                <div className="col-md-4">
                  <SoftBox bgcolor="info.light" borderRadius="md">
                    <SoftTypography variant="body2" color="info" fontWeight="bold">
                      {currencySymbol} {formatNumber(totals.inHandAnnual)}
                    </SoftTypography>
                  </SoftBox>
                </div>
              </div>
            </SoftBox>
          )}

          {/* Save Button */}
          <div className="d-flex justify-content-end mt-3">
            {canField("save") && (
              <SoftButton
                disabled={
                  loading ||
                  !value?.is_salarystructure ||
                  !permSet.has("UserProfile.SalaryStructure.save")
                }
                variant="gradient"
                color="info"
                size="medium"
                onClick={handleConfirmUpdate}
              >
                {loading ? "Saving..." : "Save"}
              </SoftButton>
            )}
          </div>
        </div>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onClose={handleCloseDialog} maxWidth="sm" fullWidth PaperProps={{
        sx: {
          borderRadius: "16px",
          padding: "8px",
          boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
        },

      }} BackdropProps={{
        sx: {
          backdropFilter: "blur(6px)",
          backgroundColor: "rgba(0,0,0,0.3)",
        },
      }}>
        <DialogTitle id="alert-dialog-title" sx={{ pb: 0 }}> <SoftTypography variant="h5" fontWeight="bold">
          {confirmDialog.title}
        </SoftTypography></DialogTitle>
        <DialogContent sx={{ px: 3, pb: 2 }}>
          <DialogContentText id="alert-dialog-description">
            {confirmDialog.message}
          </DialogContentText>

          {/* <SoftBox mt={2} p={2} bgcolor="grey.100" borderRadius="md">
            <SoftTypography variant="subtitle2" fontWeight="bold" gutterBottom>
              Salary Summary:
            </SoftTypography>

            <Grid container spacing={1}>
              <Grid item xs={6}>
                <SoftTypography variant="caption" color="text">
                  Currency:
                </SoftTypography>
              </Grid>
              <Grid item xs={6}>
                <SoftTypography variant="caption" fontWeight="bold">
                  {selectedCurrency} ({currencySymbol})
                </SoftTypography>
              </Grid>

              <Grid item xs={12} mt={1}>
                <hr style={{ margin: "4px 0" }} />
              </Grid>
              <Grid item xs={6}>
                <SoftTypography variant="caption" fontWeight="bold" color="primary">
                  Total CTC (Monthly):
                </SoftTypography>
              </Grid>
              <Grid item xs={6}>
                <SoftTypography variant="caption" fontWeight="bold" color="primary">
                  {currencySymbol} {formatNumber(totals.earningsMonthly)}
                </SoftTypography>
              </Grid>

              <Grid item xs={12} mt={0.5}>
                <SoftTypography variant="caption" fontWeight="bold" color="error">
                  Deductions:
                </SoftTypography>
              </Grid>

              {deductionComponents.map(({ key, label }) => {
                const monthlyKey = `${key}_monthly`;
                const monthlyValue = parseFloat(formData?.[monthlyKey] || 0);
                if (monthlyValue <= 0) return null;
                return (
                  <React.Fragment key={key}>
                    <Grid item xs={6}>
                      <SoftTypography variant="caption" color="text">
                        {label}:
                      </SoftTypography>
                    </Grid>
                    <Grid item xs={6}>
                      <SoftTypography variant="caption" fontWeight="medium">
                        - {currencySymbol} {formatNumber(monthlyValue)}
                      </SoftTypography>
                    </Grid>
                  </React.Fragment>
                );
              })}

              <Grid item xs={12}>
                <hr style={{ margin: "8px 0" }} />
              </Grid>

              <Grid item xs={6}>
                <SoftTypography variant="caption" fontWeight="bold" color="info">
                  Net Salary (Monthly):
                </SoftTypography>
              </Grid>
              <Grid item xs={6}>
                <SoftTypography variant="caption" fontWeight="bold" color="info">
                  {currencySymbol} {formatNumber(totals.inHandMonthly)}
                </SoftTypography>
              </Grid>

              <Grid item xs={6}>
                <SoftTypography variant="caption" fontWeight="bold" color="info">
                  Net Salary (Annual):
                </SoftTypography>
              </Grid>
              <Grid item xs={6}>
                <SoftTypography variant="caption" fontWeight="bold" color="info">
                  {currencySymbol} {formatNumber(totals.inHandAnnual)}
                </SoftTypography>
              </Grid>
            </Grid>
          </SoftBox> */}

          {/* <Card sx={{ mt: 2, borderRadius: "12px", boxShadow: "0 4px 20px rgba(0,0,0,0.08)" }}> */}
          <Card
            sx={{
              mt: 2,
              borderRadius: "16px",
              boxShadow: "0 10px 40px rgba(0,0,0,0.12)",
              background: "linear-gradient(145deg, #ffffff, #f0f4f8)",
            }}
          >
            <SoftBox p={2.5}>
              <SoftBox display="flex" alignItems="center" mb={2}>
                <SoftBox
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "10px",
                    background: "linear-gradient(135deg, #6C5CE7, #00CEC9)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    fontSize: "16px",
                    mr: 1.5,
                  }}
                >
                  ₹
                </SoftBox>

                <SoftTypography variant="h6" fontWeight="bold">
                  Salary Summary
                </SoftTypography>
              </SoftBox>

              <Grid container spacing={2}>

                {/* CTC */}
                <Grid item xs={12}>
                  {/* <Card sx={{ p: 2, borderRadius: "10px", background: "#F8FAFC" }}> */}
                  <Card
                    sx={{
                      p: 2,
                      borderRadius: "12px",
                      background: "#F8FAFC",
                      transition: "0.3s",
                      "&:hover": {
                        transform: "translateY(-3px)",
                        boxShadow: "0 8px 25px rgba(0,0,0,0.1)",
                      },
                    }}
                  >
                    <SoftTypography variant="caption" color="text">
                      Total CTC (Monthly)
                    </SoftTypography>
                    <SoftTypography variant="h5" fontWeight="bold">
                      {currencySymbol} {formatNumber(totals.earningsMonthly)}
                    </SoftTypography>
                  </Card>
                </Grid>

                {/* Deductions */}
                <Grid item xs={12}>
                  {/* <Card sx={{ p: 2, borderRadius: "10px", background: "#FFF5F5" }}> */}
                  <Card
                    sx={{
                      p: 2,
                      borderRadius: "12px",
                      background: "rgba(255, 0, 0, 0.04)",
                      border: "1px solid rgba(255,0,0,0.1)",
                    }}
                  >
                    <SoftTypography variant="caption" color="error">
                      Deductions
                    </SoftTypography>

                    {deductionComponents.map(({ key, label }) => {
                      const monthlyKey = `${key}_monthly`;
                      const monthlyValue = parseFloat(formData?.[monthlyKey] || 0);
                      if (monthlyValue <= 0) return null;

                      return (
                        <SoftBox key={key} display="flex" justifyContent="space-between">
                          <SoftTypography variant="caption">{label}</SoftTypography>
                          <SoftTypography variant="caption" fontWeight="bold" color="error">
                            - {currencySymbol} {formatNumber(monthlyValue)}
                          </SoftTypography>
                        </SoftBox>
                      );
                    })}
                  </Card>
                </Grid>

                {/* Net Salary */}
                <Grid item xs={12}>
                  <Card
                    sx={{
                      p: 2,
                      borderRadius: "10px",
                      background: "linear-gradient(135deg, #00B894, #0984E3)",
                      boxShadow: "0 10px 30px rgba(0, 184, 148, 0.4)",
                      color: "#fff",
                    }}
                  >
                    <SoftTypography variant="caption">
                      Net Salary (Monthly)
                    </SoftTypography>
                    {/* <SoftTypography variant="h5" fontWeight="bold"> */}
                    <SoftTypography variant="h4" fontWeight="bold" sx={{ letterSpacing: "0.5px" }}>
                      {currencySymbol} {formatNumber(totals.inHandMonthly)}
                    </SoftTypography>

                    <SoftTypography variant="caption" mt={1} display="block">
                      Annual: {currencySymbol} {formatNumber(totals.inHandAnnual)}
                    </SoftTypography>
                  </Card>
                </Grid>

              </Grid>
            </SoftBox>
          </Card>

          <SoftTypography variant="caption" color="text" mt={2} display="block">
            Note: This will update the employee’s salary structure.
          </SoftTypography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: "space-between" }}>
          <SoftButton onClick={handleCloseDialog} color="secondary" size="small">
            Cancel
          </SoftButton>
          <SoftButton onClick={handleUpdate} color="info" size="small" autoFocus disabled={loading}>
            {loading ? "Updating..." : "Confirm Update"}
          </SoftButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

SalaryStructure.propTypes = {
  value: PropTypes.object,
  onChange: PropTypes.func,
  onUpdate: PropTypes.func,
  loading: PropTypes.bool,
  setLoading: PropTypes.func,
  onNotify: PropTypes.func,
};

export default SalaryStructure;
